/**
 * NiV Leads Page — pipeline from first contact to a linked clinic.
 *
 * Rendered as the "clients" tab inside NivDashboardPage (tab id unchanged —
 * localStorage last-tab + badges depend on it). Row click opens the standalone
 * lead page at /dashboard/niv/lead/:phoneKey.
 * Restricted to NiV admins — all queries skip when !isAuthorized.
 */

import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { MagnifyingGlass, ArrowClockwise, Plus, Funnel, ArrowsDownUp, Printer } from "@phosphor-icons/react";
import ContentLoader from "../../../../components/shared/ContentLoader";
import { selectTherapistName } from "../../../../store/slices/authSlice";
import { useGetProspectsBoardQuery, useNivPrintListPdfMutation } from "../../../../store/services/nivApi";
import { useToast } from "../../../../contexts/ToastContext";
import { buildLeadsPrintPayload } from "../shared/printModels";
import { openPdfInNewTab } from "../shared/openPdf";
import { PROSPECT_SOURCE_LABELS } from "../../../../services/nivFirebase";
import { getProfession } from "../../../../data/startPageContent";
import ProspectList from "./ProspectList";
import ProspectFormModal from "./ProspectFormModal";
import "./NivClientsPage.css";

/** E164 "+972…" → URL segment "972…" */
export const prospectIdToPhoneKey = (id) => String(id || "").replace(/^\+/, "");

/** Normalize any typed phone (05…, +972…, with dashes/spaces) to digits-only local form for search matching. */
const phoneToLocalDigits = (raw) => {
  if (!raw) return "";
  let digits = String(raw).replace(/\D/g, "");
  if (digits.startsWith("972")) digits = "0" + digits.slice(3);
  return digits;
};

const SORT_OPTIONS = [
  { value: "incoming_desc", label: "תאריך פנייה — חדש לישן" },
  { value: "incoming_asc", label: "תאריך פנייה — ישן לחדש" },
  { value: "activity_desc", label: "פעילות אחרונה" },
  { value: "name_asc", label: "שם א–ת" },
];
const DEFAULT_SORT = "incoming_desc";
const SORT_STORAGE_KEY = "niv_leads_sort";

const toMs = (v) => {
  if (!v) return NaN;
  const t = new Date(v).getTime();
  return Number.isNaN(t) ? NaN : t;
};

/**
 * Lead incoming date = earliest known contact. The prospect doc's createdAt
 * can be the *newest* merged call request, so take the min across createdAt
 * and every linked call request.
 */
const getIncomingMs = (p) => {
  const candidates = [toMs(p.createdAt), ...(p.callRequests || []).map((r) => toMs(r.createdAt))].filter(
    (t) => !Number.isNaN(t)
  );
  return candidates.length ? Math.min(...candidates) : 0;
};

const sortProspects = (list, sortBy) => {
  const arr = [...list];
  switch (sortBy) {
    case "incoming_asc":
      return arr.sort((a, b) => getIncomingMs(a) - getIncomingMs(b));
    case "activity_desc":
      return arr.sort(
        (a, b) => (toMs(b.updatedAt || b.createdAt) || 0) - (toMs(a.updatedAt || a.createdAt) || 0)
      );
    case "name_asc":
      return arr.sort((a, b) =>
        (a.fullName || a.phone || a.id || "").localeCompare(b.fullName || b.phone || b.id || "", "he")
      );
    case "incoming_desc":
    default:
      return arr.sort((a, b) => getIncomingMs(b) - getIncomingMs(a));
  }
};

const readStoredSort = () => {
  try {
    const v = localStorage.getItem(SORT_STORAGE_KEY);
    return SORT_OPTIONS.some((o) => o.value === v) ? v : DEFAULT_SORT;
  } catch {
    return DEFAULT_SORT;
  }
};

function NivClientsPage({ isAuthorized }) {
  const navigate = useNavigate();
  const { showError } = useToast();
  const therapistName = useSelector(selectTherapistName);
  const author = therapistName || "NiV";

  const [searchTerm, setSearchTerm] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [sortBy, setSortBy] = useState(readStoredSort);
  const [showFormModal, setShowFormModal] = useState(false);

  const handleSortChange = (value) => {
    setSortBy(value);
    try {
      localStorage.setItem(SORT_STORAGE_KEY, value);
    } catch {
      /* storage unavailable — keep in-memory only */
    }
  };

  const {
    data: board,
    isLoading: isBoardLoading,
    isFetching: isBoardFetching,
    refetch: refetchBoard,
  } = useGetProspectsBoardQuery(undefined, { skip: !isAuthorized });

  const prospects = useMemo(() => board?.prospects || [], [board]);
  const unlinkedRequests = useMemo(() => board?.unlinkedRequests || [], [board]);

  const filteredProspects = useMemo(() => {
    let list = prospects;
    if (sourceFilter !== "all") {
      list = list.filter((p) => (p.source || "manual") === sourceFilter);
    }
    const term = searchTerm.trim().toLowerCase();
    if (term) {
      const termDigits = phoneToLocalDigits(term);
      list = list.filter((p) => {
        const professionLabel = p.profession ? getProfession(p.profession)?.label || p.profession : "";
        const nameMatch =
          (p.fullName || "").toLowerCase().includes(term) ||
          (p.clinicName || "").toLowerCase().includes(term) ||
          (p.email || "").toLowerCase().includes(term) ||
          (p.profession || "").toLowerCase().includes(term) ||
          professionLabel.toLowerCase().includes(term);
        const phoneMatch =
          termDigits.length >= 3 && phoneToLocalDigits(p.phone || p.id).includes(termDigits);
        return nameMatch || phoneMatch;
      });
    }
    return sortProspects(list, sortBy);
  }, [prospects, searchTerm, sourceFilter, sortBy]);

  const openLead = (id) => navigate(`/dashboard/niv/lead/${prospectIdToPhoneKey(id)}`);

  const [printList, { isLoading: isPrinting }] = useNivPrintListPdfMutation();
  const handlePrint = async () => {
    const tab = openPdfInNewTab();
    try {
      const payload = buildLeadsPrintPayload(filteredProspects, {
        sourceLabel: sourceFilter !== "all" ? PROSPECT_SOURCE_LABELS[sourceFilter] : null,
        searchTerm: searchTerm.trim(),
        sortLabel: SORT_OPTIONS.find((o) => o.value === sortBy)?.label,
      });
      const result = await printList(payload).unwrap();
      tab.show(result.pdfUrl);
    } catch (error) {
      tab.close();
      console.error("[CK-9824] Error printing leads list:", {
        errorCode: "CK-9824",
        errorMessage: error?.message,
        rowCount: filteredProspects.length,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בהכנת קובץ ההדפסה");
    }
  };

  if (!isAuthorized) return null;

  return (
    <div className="nivcl-page">
      {/* Header */}
      <div className="nivcl-header">
        <div className="nivcl-header-titles">
          <h2 className="nivcl-title">לידים</h2>
          <p className="nivcl-subtitle">מהפנייה הראשונה ועד קליניקה מקושרת</p>
        </div>
        <div className="nivcl-header-actions">
          <div className="nivcl-search-box">
            <MagnifyingGlass size={16} />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="חיפוש לפי שם, טלפון, קליניקה או אימייל…"
              aria-label="חיפוש לידים"
            />
          </div>
          <div className="nivcl-filter-select">
            <Funnel size={14} />
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              aria-label="סינון לפי מקור"
            >
              <option value="all">כל המקורות</option>
              {Object.entries(PROSPECT_SOURCE_LABELS).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          <div className="nivcl-filter-select">
            <ArrowsDownUp size={14} />
            <select
              value={sortBy}
              onChange={(e) => handleSortChange(e.target.value)}
              aria-label="מיון לידים"
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            className="nivcl-icon-btn"
            onClick={() => refetchBoard()}
            disabled={isBoardFetching}
            title="רענון"
            aria-label="רענון"
          >
            <ArrowClockwise size={16} className={isBoardFetching ? "nivcl-spin" : ""} />
          </button>
          <button
            type="button"
            className="nivcl-secondary-btn"
            onClick={handlePrint}
            disabled={isPrinting || filteredProspects.length === 0}
            title="הדפסת הרשימה המסוננת כ-PDF"
          >
            <Printer size={14} weight="fill" />
            <span>{isPrinting ? "מכין…" : "הדפסה"}</span>
          </button>
          <button type="button" className="nivcl-primary-btn" onClick={() => setShowFormModal(true)}>
            <Plus size={14} weight="bold" />
            <span>ליד חדש</span>
          </button>
        </div>
      </div>

      {isBoardLoading ? (
        <ContentLoader message="טוען לידים…" />
      ) : (
        <ProspectList
          prospects={filteredProspects}
          unlinkedRequests={unlinkedRequests}
          counts={board?.counts || {}}
          author={author}
          onSelect={(p) => openLead(p.id)}
        />
      )}

      {showFormModal && (
        <ProspectFormModal
          author={author}
          onClose={() => setShowFormModal(false)}
          onCreated={(id) => {
            setShowFormModal(false);
            openLead(id);
          }}
        />
      )}
    </div>
  );
}

export default NivClientsPage;
